import { useState, type ReactNode } from "react";
import { money, propPayoutTax, D } from "@ftr/tax-core";
import { Figure } from "./figure.tsx";
import { usd } from "../lib/format.ts";

const STEPS = [5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000, 60000, 75000, 100000, 125000, 150000];

/**
 * Live demo: drag the year's prop profit and watch the set-aside change, drawn as a bar split into the federal tax to
 * set aside (gold) and what's left after it (green). Single filer, no other income. Drawn as a
 * file folder with an "Example" tab, resting on the two forms the figures end up on; children (the key figures) fill
 * its bottom.
 */
export function HeroDemo({ children }: { children?: ReactNode }) {
  const [i, setI] = useState(6);
  const profit = STEPS[i]!;
  const r = propPayoutTax(
    {
      status: "single",
      wages: money("0"),
      traderSocialSecurityWages: money("0"),
      medicareWages: money("0"),
      withholding: money("0"),
      interest: money("0"),
      otherOrdinaryIncome: money("0"),
      otherPropNetProfit: money("0"),
      net1256: money("0"),
      qbi: { include: true, specifiedService: true },
    },
    new D(profit),
    money("0"),
  );
  if (r.state !== "computed" || !r.setAsideRate) return null;
  const setAside = D.max(r.taxOnPayouts, 0);
  const left = new D(profit).minus(setAside);
  const share = D.min(setAside.div(profit), 1).times(100).toDecimalPlaces(2).toString() + "%";
  return (
    <div className="demo-stack">
      <div className="demo-sheets" aria-hidden="true">
        <span className="demo-sheet demo-sheet--back">Schedule C</span>
        <span className="demo-sheet">Form 6781</span>
      </div>
      <div className="demo">
        <span className="demo-tab" aria-hidden="true">
          Example
        </span>
        <div className="demo-top">
          <span className="demo-label">Prop profit this year</span>
          <span className="demo-amount">{usd(new D(profit))}</span>
        </div>
        <input
          className="demo-range"
          type="range"
          min={0}
          max={STEPS.length - 1}
          step={1}
          value={i}
          onChange={(e) => setI(Number(e.target.value))}
          aria-label="Prop profit this year"
          aria-valuetext={usd(new D(profit))}
        />
        <div className="demo-split">
          <div className="demo-split-bar" aria-hidden="true">
            <span className="demo-split-set" style={{ width: share }} />
          </div>
          <div className="demo-split-key">
            <span>
              <i className="demo-split-dot demo-split-dot--set" aria-hidden="true" />
              Set aside <strong>{usd(setAside)}</strong>
            </span>
            <span>
              <i className="demo-split-dot" aria-hidden="true" />
              Left after federal tax <strong>{usd(left)}</strong>
            </span>
          </div>
        </div>
        <div className="demo-out">
          <div>
            <span className="demo-label">Set aside</span>
            <span className="demo-big">
              <Figure value={r.setAsideRate} kind="pct" places={1} />
            </span>
          </div>
          <div>
            <span className="demo-label">Federal tax</span>
            <span className="demo-mid">
              <Figure value={r.taxOnPayouts} />
            </span>
          </div>
        </div>
        <p className="demo-foot">Single filer, no other income, 2026 rules. Your numbers will differ, so try the full calculator.</p>
        {children}
      </div>
    </div>
  );
}
